import type {
  ApplicationValidationError,
  ConfigurationOperation,
  DependencyValidationError,
} from '@festina/configuration-general-types/configuration'
import { dateUtils, type Optional } from '@festina-life/core'

import type { ConfigurationItem } from '../../types/common'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { useConfigurationOperationErrorsMap } from '../../uiModels/errors/useConfigurationOperationErrorsMap'
import type { OperationWithSourceInfo } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { LocationAwareOperationLink } from '../configurationOperationLink/LocationAwareOperationLink'
import {
  listIdsWithOperationsOrErrors,
  resolveOperationLocation,
} from './helpers'
import type { OperationsListVariant } from './types'

type LinkType<T extends ConfigurationOperation> =
  | ConfigurationItem
  | ((
      operation: Optional<T>,
      operationClass: Optional<string>,
    ) => ConfigurationItem)

/** How the link of a configuration item is rendered. */
type LinkDescriptor<T extends ConfigurationOperation> = {
  type: LinkType<T>
  /** Item the link navigates to. Defaults to the item the operations are grouped by. */
  itemId?: (itemId: string) => string
  /** Label of the link. Defaults to the item the link navigates to. */
  itemLabel?: (itemId: string) => string
  /** Path within the item the link navigates to, for items viewed as a whole. */
  propertyPath?: (itemId: string) => string
  /** Date the created version comes into effect, for versioned items. */
  fromDate?: (operation: T) => Optional<string>
}

type Props<T extends ConfigurationOperation> = {
  operations: OperationWithSourceInfo<T>[]
  errors: ValidationErrorWithLocation<
    ApplicationValidationError | DependencyValidationError
  >[]
  variant: OperationsListVariant
  /** Configuration item an operation belongs to. Operations and errors are grouped by it. */
  itemIdFromOperation: (operation: T) => string
  link: LinkDescriptor<T>
}

/**
 * Every operation of every configuration item of one kind, as links carrying the
 * validation errors reported for them. Errors without an operation are listed as
 * well, as they belong to operations of the branch rather than of the workspace.
 */
export const ConfigurationItemOperationsWithErrors = <
  T extends ConfigurationOperation,
>({
  operations,
  errors,
  variant,
  itemIdFromOperation,
  link,
}: Props<T>) => {
  const { workspaceKey, branchKey } = useOperationsContext()

  const errorsByItemId = useConfigurationOperationErrorsMap({
    errors,
    operations,
    idFromOperation: (operation) =>
      itemIdFromOperation(operation.internalOperation),
    idFromError: (error) => error.operationName ?? '',
  })

  const allItemIds = listIdsWithOperationsOrErrors(
    operations.map(({ internalOperation }) =>
      itemIdFromOperation(internalOperation),
    ),
    errorsByItemId,
  )

  return allItemIds.map((itemId) => {
    const itemOperations = operations.filter(
      ({ internalOperation }) =>
        itemIdFromOperation(internalOperation) === itemId,
    )
    const itemErrors = errorsByItemId.get(itemId)

    const operationKeySet = listIdsWithOperationsOrErrors(
      itemOperations.map(
        ({ internalOperation }) => internalOperation.operationKey,
      ),
      itemErrors,
    )

    return operationKeySet.map((operationKey) => {
      const operation = itemOperations.find(
        ({ internalOperation }) =>
          internalOperation.operationKey === operationKey,
      )
      const operationErrors = itemErrors?.get(operationKey)
      const errorCount = operationErrors?.length ?? 0

      const shouldHideOperationLink =
        variant === 'errors' ? !errorCount : !operation

      const errorFromDate = link.fromDate
        ? operationErrors?.at(0)?.error.operationFromDate
        : undefined
      const fromDateString = operation
        ? link.fromDate?.(operation.internalOperation)
        : errorFromDate

      const linkType =
        typeof link.type === 'function'
          ? link.type(
              operation?.internalOperation,
              operationErrors?.at(0)?.error.operationClass,
            )
          : link.type

      const { layer, block } = resolveOperationLocation(
        operation,
        operationErrors,
      )

      return (
        !shouldHideOperationLink && (
          <LocationAwareOperationLink
            key={operationKey}
            layer={layer}
            block={block}
            itemId={link.itemId?.(itemId) ?? itemId}
            itemLabel={link.itemLabel?.(itemId)}
            propertyPath={link.propertyPath?.(itemId)}
            type={linkType}
            workspaceKey={operation ? workspaceKey : undefined} // If there's no operation then the operation belongs to the branch and not the workspace
            branchKey={workspaceKey?.branchKey ?? branchKey}
            errorCount={errorCount}
            fromDate={
              fromDateString
                ? dateUtils.dateUtcAsLocal(fromDateString).toDate()
                : undefined
            }
            operation={operation?.internalOperation}
            operationKey={operationKey}
            isDeleteEnabled={variant === 'operations'}
            isDraft={!!operation?.isDraft}
          />
        )
      )
    })
  })
}
